import { type KeyboardEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { X } from 'lucide-react';
import { FormDialog } from '@/shared/ui/FormDialog';
import { FilterChipPopover } from '@/shared/ui';
import { useDebounce } from '@/shared/hooks';
import { advertiserLabel, connectorAdvertisersQueryOptions } from '@/entities/media-campaign';
import type { SyncCadence } from '@/entities/media-connector';
import {
  SYNC_CADENCE_DESCRIPTIONS,
  SYNC_CADENCE_LABELS,
  SYNC_CADENCE_OPTIONS,
} from '@/entities/media-connector';

interface EditAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Needed to ask the platform which advertisers this connection reaches. */
  connectorId: string | null;
  initialAdvertiserId: string | null;
  initialAdvertiserName: string | null;
  initialTitle: string;
  initialTags: string[];
  initialSyncCadence: SyncCadence;
  initialDsaBeneficiary: string;
  initialDsaPayor: string;
  onSubmit: (input: {
    title: string;
    tags: string[];
    syncCadence: SyncCadence;
    dsaBeneficiary: string | null;
    dsaPayor: string | null;
    advertiserId: string | null;
    advertiserName: string | null;
  }) => void;
  loading?: boolean;
}

export function EditAccountDialog({
  open,
  onOpenChange,
  connectorId,
  initialAdvertiserId,
  initialAdvertiserName,
  initialTitle,
  initialTags,
  initialSyncCadence,
  initialDsaBeneficiary,
  initialDsaPayor,
  onSubmit,
  loading,
}: EditAccountDialogProps) {
  const { t } = useTranslation('knowledge');

  const [title, setTitle] = useState(initialTitle);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [tagInput, setTagInput] = useState('');
  const [syncCadence, setSyncCadence] = useState<SyncCadence>(initialSyncCadence);
  const [dsaBeneficiary, setDsaBeneficiary] = useState(initialDsaBeneficiary);
  const [dsaPayor, setDsaPayor] = useState(initialDsaPayor);
  const [advertiser, setAdvertiser] = useState<{ id: string; name: string } | null>(null);
  /**
   * Handed to the platform rather than used to filter here: one DV360 partner answers
   * with hundreds of advertisers, which is neither worth fetching whole nor scrollable.
   */
  const [advertiserSearch, setAdvertiserSearch] = useState('');
  const {
    data: advertiserList,
    isFetching: isFetchingAdvertisers,
    isError: advertisersFailed,
    refetch: refetchAdvertisers,
  } = useQuery({
    ...connectorAdvertisersQueryOptions(connectorId, useDebounce(advertiserSearch, 300)),
    enabled: open && Boolean(connectorId),
  });

  useEffect(() => {
    if (open) {
      setTitle(initialTitle);
      setTags(initialTags);
      setSyncCadence(initialSyncCadence);
      setDsaBeneficiary(initialDsaBeneficiary);
      setDsaPayor(initialDsaPayor);
      setAdvertiser(
        initialAdvertiserId
          ? { id: initialAdvertiserId, name: initialAdvertiserName ?? initialAdvertiserId }
          : null,
      );
      setAdvertiserSearch('');
      setTagInput('');
    }
  }, [
    open,
    initialTitle,
    initialTags,
    initialSyncCadence,
    initialDsaBeneficiary,
    initialDsaPayor,
    initialAdvertiserId,
    initialAdvertiserName,
  ]);

  const addTag = (value: string) => {
    const trimmed = value.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      addTag(tagInput);
    }
    if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      setTags(tags.slice(0, -1));
    }
  };

  const handleSubmit = () => {
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      tags,
      syncCadence,
      // Emptied on purpose clears the declaration rather than leaving the old one in place.
      dsaBeneficiary: dsaBeneficiary.trim() || null,
      dsaPayor: dsaPayor.trim() || null,
      advertiserId: advertiser?.id ?? null,
      advertiserName: advertiser?.name ?? null,
    });
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('mediaConnectors.editAccountDialog.editAccount')}
    >
      <FormDialog.Body>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-account-title">
              {t('mediaConnectors.editAccountDialog.title')}
            </Label>
            <Input
              id="edit-account-title"
              placeholder={t('mediaConnectors.editAccountDialog.egUsMarketApac')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-account-tags">{t('mediaConnectors.editAccountDialog.tags')}</Label>
            <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 focus-within:ring-1 focus-within:ring-ring">
              {tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="ghost"
                  roundness="full"
                  className="gap-1 border-border-light px-2 py-0.5 text-xs"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-muted"
                  >
                    <X className="size-3" />
                  </button>
                </Badge>
              ))}
              <input
                id="edit-account-tags"
                type="text"
                placeholder={
                  tags.length === 0 ? t('mediaConnectors.editAccountDialog.typeAndPressEnter') : ''
                }
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                onBlur={() => {
                  if (tagInput.trim()) addTag(tagInput);
                }}
                className="min-w-[120px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-sync-cadence">
              {t('mediaConnectors.editAccountDialog.syncFrequency')}
            </Label>
            <Select value={syncCadence} onValueChange={(v) => setSyncCadence(v as SyncCadence)}>
              <SelectTrigger id="edit-sync-cadence" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SYNC_CADENCE_OPTIONS.map((cadence) => (
                  <SelectItem key={cadence} value={cadence}>
                    {SYNC_CADENCE_LABELS[cadence]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {SYNC_CADENCE_DESCRIPTIONS[syncCadence]}
            </p>
          </div>

          {/* Asked for once per account rather than per plan: this is who the advertiser
              is, which does not change between campaigns. A failed read is shown, not hidden:
              the server answers unsupported before signing in, so a failure is always a
              chooser that exists, and hiding it left a manager account no way to finish. */}
          {(advertisersFailed || advertiserList?.supported) && (
            <div className="space-y-1.5">
              <Label>{t('mediaConnectors.editAccountDialog.advertiser')}</Label>
              {advertisersFailed ? (
                <div role="alert" className="flex items-start justify-between gap-2">
                  <p className="text-xs text-destructive">
                    {t('mediaConnectors.editAccountDialog.advertisersCouldNotLoad')}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isFetchingAdvertisers}
                    onClick={() => void refetchAdvertisers()}
                  >
                    {t('mediaConnectors.editAccountDialog.tryAgain')}
                  </Button>
                </div>
              ) : (
                <FilterChipPopover<string>
                  mode="single"
                  label={t('mediaConnectors.editAccountDialog.advertiser')}
                  triggerLabelStyle="value"
                  triggerAriaLabel={t('mediaConnectors.editAccountDialog.selectAnAdvertiser')}
                  searchPlaceholder={t('mediaConnectors.editAccountDialog.searchAdvertisers')}
                  variant="ghost"
                  hideReset
                  resetOptionLabel={
                    advertiser
                      ? advertiserLabel(advertiser)
                      : t('mediaConnectors.editAccountDialog.selectAnAdvertiser')
                  }
                  options={advertiserList.items.map((item) => ({
                    value: item.id,
                    label: advertiserLabel(item),
                  }))}
                  value={advertiser?.id ?? null}
                  onChange={(next) => {
                    const picked = advertiserList.items.find((item) => item.id === next);
                    if (picked) setAdvertiser({ id: picked.id, name: picked.name });
                  }}
                  asyncOptions={{
                    search: advertiserSearch,
                    onSearchChange: setAdvertiserSearch,
                    isLoading: isFetchingAdvertisers,
                  }}
                />
              )}
              <p className="text-xs text-muted-foreground">
                {t('mediaConnectors.editAccountDialog.advertiserHelp')}
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="edit-dsa-beneficiary">
              {t('mediaConnectors.editAccountDialog.advertiserEuDisclosure')}
            </Label>
            <Input
              id="edit-dsa-beneficiary"
              placeholder={t('mediaConnectors.editAccountDialog.egMonks')}
              value={dsaBeneficiary}
              onChange={(e) => setDsaBeneficiary(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t('mediaConnectors.editAccountDialog.thePersonOrOrganisationTheAdsPromote')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-dsa-payor">
              {t('mediaConnectors.editAccountDialog.payerEuDisclosure')}
            </Label>
            <Input
              id="edit-dsa-payor"
              placeholder={t('mediaConnectors.editAccountDialog.egMonks')}
              value={dsaPayor}
              onChange={(e) => setDsaPayor(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t('mediaConnectors.editAccountDialog.whoPaysForTheAds')}
            </p>
          </div>
        </div>
      </FormDialog.Body>
      <FormDialog.Footer
        primary={{
          label: t('mediaConnectors.editAccountDialog.save'),
          onClick: handleSubmit,
          disabled: !title.trim(),
          loading,
        }}
        secondary={{
          label: t('mediaConnectors.editAccountDialog.cancel'),
          onClick: () => onOpenChange(false),
        }}
      />
    </FormDialog>
  );
}
